// Services/EmployeeServices/TeamLeaveApprovalsService.js
// Supervisor/Manager leave approval flow inside the Employee Dashboard.
// Backed by the /LeaveRequest/Supervisor/* endpoints (per Mudassir's handoff doc).
import { useState } from "react";
import { message } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

export default function TeamLeaveApprovalsService() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  // Pending leave requests submitted by the logged-in employee's direct reports.
  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API.leaveRequest.supervisorPendingApprovals);
      setPendingRequests(Array.isArray(response.data) ? response.data : []);
      return response.data;
    } catch (error) {
      console.error("Error fetching team pending approvals:", error);
      message.error("Failed to load team leave requests");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Team availability for one pending request (overlap warning data).
  // Non-blocking: a failure here should not stop the supervisor from approving.
  const fetchTeamAvailability = async (leaveRequestId) => {
    const response = await axiosInstance.get(
      API.leaveRequest.supervisorTeamAvailability(leaveRequestId)
    );
    return response.data;
  };

  // payload: { leaveRequestId, isApproved, approverRemarks }
  const submitReview = async (payload) => {
    try {
      setReviewing(true);
      const response = await axiosInstance.put(API.leaveRequest.supervisorReview, payload);
      message.success(payload.isApproved ? "Leave request approved" : "Leave request rejected");
      return response.data;
    } catch (error) {
      console.error("Error submitting supervisor review:", error);
      const detail = error.response?.data?.details;
      message.error(detail || "Failed to submit the review");
      throw error;
    } finally {
      setReviewing(false);
    }
  };

  // All team leave requests for the supervisor's direct reports, with optional
  // filters (year, status, employeeId, upcomingOnly). Per handoff section 7.
  const fetchTeamRequests = async (queryString) => {
    const response = await axiosInstance.get(
      API.leaveRequest.supervisorTeamRequests(queryString)
    );
    const d = response.data;
    return Array.isArray(d) ? d : d?.items || d?.requests || d?.leaveRequests || [];
  };

  return {
    pendingRequests,
    loading,
    reviewing,
    fetchPendingApprovals,
    fetchTeamAvailability,
    submitReview,
    fetchTeamRequests,
  };
}
